import React, { useEffect, useRef, useState } from 'react';

/** Vertical swipe line drawn on the map. Left of the line is the earlier date. */
export default function MapChangeSwipe({
  onChange,
  mapElement,
}: {
  onChange: (position: number) => void;
  /** Map viewport. The percentage is measured on this box so the cut matches the handle. */
  mapElement: HTMLElement | null;
}) {
  const [position, setPosition] = useState(50);
  const dragging = useRef(false);
  const frameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onChange(50);
    // Initial split only. The parent handler is stable enough for one call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveTo = (clientX: number) => {
    const frame = mapElement ?? frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width <= 0) return;
    const next = Math.max(4, Math.min(96, ((clientX - rect.left) / rect.width) * 100));
    setPosition(next);
    onChange(next);
  };

  return (
    <div
      ref={frameRef}
      className="absolute inset-x-0 top-0 z-20 pointer-events-none"
      style={{ bottom: '12.5rem' }}
    >
      <div
        className="absolute top-0 bottom-0 w-8 cursor-ew-resize pointer-events-auto touch-none"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        onPointerDown={(event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          moveTo(event.clientX);
        }}
        onPointerMove={(event) => {
          if (!dragging.current) return;
          moveTo(event.clientX);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
        role="slider"
        aria-label="Drag to compare before and after"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
      >
        <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.45)]" />
        <div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white text-[#111827] shadow-lg">
          <i className="ri-arrow-left-right-line text-lg" aria-hidden />
        </div>
        <span className="absolute top-3 right-[calc(50%+14px)] rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          Before
        </span>
        <span className="absolute top-3 left-[calc(50%+14px)] rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          After
        </span>
      </div>
    </div>
  );
}
